import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiFetch';

// Stil tanımlamaları için yeni bir CSS modülü oluşturalım
import './AuthContext.css';  // Bu dosyayı da oluşturacağız

const AuthContext = createContext(null);

const API_URL = 'https://api.glowytics.com';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(() => {
    // LocalStorage'dan başlangıç değerini al
    const saved = localStorage.getItem('selectedBrand');
    return saved ? JSON.parse(saved) : null;
  });
  const navigate = useNavigate();

  // Debug için useEffect ekleyelim
  useEffect(() => {
    console.log('Selected Brand changed:', selectedBrand);
  }, [selectedBrand]);

  /**
   * Seçili markanın rolünü sunucudaki güncel haliyle değiştirir. Sadece
   * `currentUserRole` (ve marka adı gibi alanlar) değiştiyse yazar; kısıtlı
   * roller marka bazında olduğu için eskimiş bir rol yanlış menü gösterir.
   */
  const markaRolunuTazele = async (kayitli) => {
    if (!kayitli?.id) return;
    try {
      const r = await apiFetch(`${API_URL}/api/brands`);
      if (!r.ok) return;
      const d = await r.json();
      const taze = (d.brands || []).find((b) => b.id === kayitli.id);
      if (!taze || (taze.currentUserRole === kayitli.currentUserRole && taze.currency === kayitli.currency)) return;
      setSelectedBrand(taze);
      localStorage.setItem('selectedBrand', JSON.stringify(taze));
    } catch (e) {
      // Sessiz: rol tazeleme bir kolaylıktır, asıl kilit backend'de.
    }
  };

  /**
   * `?brand=<id>` ile gelen linkler (ör. Shopify app'teki "Open Glowytics") o markayı
   * seçili açar. Giriş ekranından geçilse bile kaybolmasın diye sessionStorage'da bekler;
   * kullanıcının erişimi olmayan bir id sessizce yok sayılır.
   */
  const bekleyenMarkayiUygula = async () => {
    const id = sessionStorage.getItem('pendingBrand');
    if (!id) return;
    try {
      const r = await apiFetch(`${API_URL}/api/brands`);
      if (!r.ok) return;
      const d = await r.json();
      const marka = (d.brands || []).find((b) => b.id === id);
      sessionStorage.removeItem('pendingBrand');
      if (!marka) return;
      setSelectedBrand(marka);
      localStorage.setItem('selectedBrand', JSON.stringify(marka));
    } catch (e) {
      // Sessiz: link kolaylıktır; marka seçici her zaman elle kullanılabilir.
    }
  };

  useEffect(() => {
    const urlBrand = new URLSearchParams(window.location.search).get('brand');
    if (urlBrand) {
      sessionStorage.setItem('pendingBrand', urlBrand);
      const url = new URL(window.location.href);
      url.searchParams.delete('brand');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        const response = await apiFetch('https://api.glowytics.com/api/auth/check');
        
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          
          // Kayıtlı brand'i kontrol et
          const savedBrand = localStorage.getItem('selectedBrand');
          if (savedBrand) {
            const kayitli = JSON.parse(savedBrand);
            setSelectedBrand(kayitli);
            // localStorage'daki kopya donmuş bir anlık görüntüdür; `currentUserRole`
            // bu arada değişmiş olabilir ve kısıtlı roller (FINANCE / GBP) buradan
            // okunuyor (RestrictedSidebar → kisitliTip). Rolü sessizce tazele.
            markaRolunuTazele(kayitli);
          }
          await bekleyenMarkayiUygula();
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (userData) => {
    setUser(userData);
    setLoading(false); // Make sure loading is set to false after login
    bekleyenMarkayiUygula();
  };

  const logout = async (redirectPath = '/login') => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      
      setUser(null);
      setSelectedBrand(null); // Brand bilgisini temizle
      
      // Clear all brand-related localStorage data
      localStorage.removeItem('selectedBrand');
      localStorage.removeItem('currentBrandId');
      localStorage.removeItem('selectedBrandId');
      localStorage.removeItem('activeSettingsSection');
      localStorage.removeItem('token');
      localStorage.removeItem('glowyAiUserId');
      localStorage.removeItem('creativeDetailData');
      
      navigate(redirectPath);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    loading,
    selectedBrand,
    setSelectedBrand: (brand) => {
      console.log('Setting new brand:', brand); // Debug log
      setSelectedBrand(brand);
    },
    login,
    logout,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="loading-logo-container">
          <img 
            src="/logoicon.png" 
            alt="Glowytics Logo" 
            className="loading-logo"
          />
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 